import type { TreeNodeData } from '@mantine/core';
import type { ModuleIndex } from './module-filter';
import type { ModuleId } from './module-labels';
import {
  compareNames,
  escapePointerSegment,
  getComponentSchemas,
  getJsonMediaSchema,
  hasAnySelected,
  HTTP_METHODS,
  isDiscriminator,
  isObject,
  NO_MODULES,
  resolveRefs,
  type JsonObject,
  type ResolvedSchema,
  toModules,
} from './openapi-utils';

/**
 * - operation: an operation, e.g. POST /beef
 * - requestBody / responses: the groups of an operation's input and responses, in the inputs view
 * - response: one status code's response
 * - property: a schema property, or the values of a map
 * - variant: one oneOf/anyOf option
 */
export type SpecNodeKind = 'operation' | 'requestBody' | 'responses' | 'response' | 'property' | 'variant';

export type SpecOperation = {
  method: string;
  path: string;
  summary?: string;
  description?: string;
  operationId?: string;
};

export type SpecNode = {
  /** Unique id of the node within the tree, built from the escaped path segments leading to it */
  value: string;
  parent: string | null;
  kind: SpecNodeKind;
  name: string;
  /** The schema for this node with any $ref resolved, keeping keywords declared alongside the $ref */
  schema: JsonObject;
  /** Component schema name, when this node was declared as a $ref */
  refName?: string;
  /** Short type summary shown next to the name, empty for nodes that only group others */
  typeLabel: string;
  /** The $ref points back at a schema already being expanded, so its children are not repeated */
  circular?: boolean;
  required: boolean;
  /** Modules declared directly on this node */
  ownModules: ModuleId[];
  /** Modules declared on this node or anywhere beneath it */
  subtreeModules: ReadonlySet<ModuleId>;
  children: string[];
  /** Set on operation nodes only */
  operation?: SpecOperation;
};

export type SpecTree = {
  data: TreeNodeData[];
  /** Every node keyed by value, in depth-first order so parents always come before their children */
  nodes: Map<string, SpecNode>;
};

const concatArrays = (a: unknown, b: unknown) => [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])];

/**
 * Combines two schemas for display: fields, required names, modules and variants from both are kept, while
 * other keywords from the second schema win.
 */
function mergeSchemas(a: JsonObject, b: JsonObject): JsonObject {
  const merged: JsonObject = { ...a, ...b };
  if (isObject(a.properties) || isObject(b.properties)) {
    merged.properties = {
      ...(isObject(a.properties) ? a.properties : {}),
      ...(isObject(b.properties) ? b.properties : {}),
    };
  }
  if ('required' in a || 'required' in b) merged.required = [...new Set(concatArrays(a.required, b.required))];
  if ('x-modules' in a || 'x-modules' in b)
    merged['x-modules'] = [...new Set(concatArrays(a['x-modules'], b['x-modules']))];
  for (const keyword of ['oneOf', 'anyOf'] as const) {
    if (keyword in a || keyword in b) merged[keyword] = concatArrays(a[keyword], b[keyword]);
  }
  return merged;
}

/**
 * Which part of each operation the tree describes:
 * - inputs: the request body, along with every response
 * - outputs: only the successful responses, placed directly under each operation
 */
export type SpecTreeView = 'inputs' | 'outputs';

const isSuccessStatus = (status: string) => /^2\d\d$/.test(status) || status === '2XX';

/** Name shown for a oneOf/anyOf option: its title, discriminator value or const tag, else its position */
function variantName(parent: JsonObject, raw: JsonObject, variant: JsonObject, position: number) {
  if (typeof variant.title === 'string') return variant.title;

  const { discriminator } = parent;
  if (isObject(discriminator) && isObject(discriminator.mapping) && typeof raw.$ref === 'string') {
    const key = Object.entries(discriminator.mapping).find(([, ref]) => ref === raw.$ref)?.[0];
    if (key !== undefined) return `${String(discriminator.propertyName)}: ${key}`;
  }

  if (isObject(variant.properties)) {
    const tag = Object.entries(variant.properties).find(([, p]) => isObject(p) && 'const' in p);
    if (tag) return `${tag[0]}: ${String((tag[1] as JsonObject).const)}`;
  }

  return `Option ${position + 1}`;
}

/**
 * Converts an openapi document into a navigable tree of operations, request bodies, responses and schema
 * properties. Component $refs are expanded in place so every field appears where it is used.
 */
export function buildSpecTree(spec: JsonObject, index: ModuleIndex, view: SpecTreeView = 'inputs'): SpecTree {
  const nodes = new Map<string, SpecNode>();
  const components = getComponentSchemas(spec);

  const resolve = (raw: JsonObject, stack: readonly string[]): ResolvedSchema => {
    const resolved = resolveRefs(raw, components, stack);
    if (resolved.circular || !Array.isArray(resolved.schema.allOf)) return resolved;

    // Flatten the composition into one schema, so its members' fields appear together as they would in a request
    const { allOf, ...outer } = resolved.schema;
    let merged: JsonObject = {};
    let mergedStack = resolved.stack;
    for (const member of allOf) {
      if (!isObject(member)) continue;
      const memberResolved = resolve(member, resolved.stack);
      // A member pointing back at a parent schema has nothing new to contribute
      if (memberResolved.circular) continue;
      merged = mergeSchemas(merged, memberResolved.schema);
      mergedStack = [...new Set([...mergedStack, ...memberResolved.stack])];
    }
    return { schema: mergeSchemas(merged, outer), refName: resolved.refName, stack: mergedStack, circular: false };
  };

  /** Records a node's children, returning the tree data for it */
  const withChildren = (node: SpecNode, children: TreeNodeData[]): TreeNodeData => {
    node.children = children.map((child) => child.value);
    return { value: node.value, label: node.name, children };
  };

  /** Registers a node that only groups others, such as an operation, before its children are added */
  const addGroupNode = (
    fields: Pick<SpecNode, 'value' | 'parent' | 'kind' | 'name' | 'ownModules' | 'subtreeModules' | 'operation'>,
  ) => {
    const node: SpecNode = { ...fields, schema: {}, typeLabel: '', required: false, children: [] };
    nodes.set(node.value, node);
    return node;
  };

  type NodeInput = {
    segment: string;
    parent: string;
    kind: SpecNodeKind;
    name: string;
    raw: JsonObject;
    required?: boolean;
    stack: readonly string[];
    /** The object x-modules lookups are keyed on, when it differs from the schema */
    moduleSource?: object;
    /** Overrides the type summary worked out from the schema */
    typeLabel?: string;
  };

  const addNode = (input: NodeInput): TreeNodeData => {
    const { segment, parent, kind, name, raw, required, stack, moduleSource, typeLabel } = input;
    const resolved = resolve(raw, stack);
    const node: SpecNode = {
      value: `${parent}/${escapePointerSegment(segment)}`,
      parent,
      kind,
      name,
      schema: resolved.schema,
      refName: resolved.refName,
      typeLabel: typeLabel ?? describeType(resolved.schema, resolved.refName),
      circular: resolved.circular || undefined,
      required: required ?? false,
      ownModules: toModules(resolved.schema['x-modules']),
      subtreeModules: index.subtreeModules.get(moduleSource ?? raw) ?? NO_MODULES,
      children: [],
    };
    // Registered before the children so the map stays in depth-first order
    nodes.set(node.value, node);

    const children = resolved.circular ? [] : addSchemaChildren(resolved.schema, node.value, resolved.stack);
    return withChildren(node, children);
  };

  const addSchemaChildren = (schema: JsonObject, parent: string, stack: readonly string[]): TreeNodeData[] => {
    const children: TreeNodeData[] = [];

    if (isObject(schema.properties)) {
      const required = new Set(Array.isArray(schema.required) ? schema.required.map(String) : []);
      // Sorted by name so fields are easy to find. Variants below keep their declared order, which is meaningful.
      const properties = Object.entries(schema.properties).sort(([a], [b]) => compareNames(a, b));
      for (const [name, child] of properties) {
        if (!isObject(child)) continue;
        children.push(
          addNode({ segment: name, parent, kind: 'property', name, raw: child, stack, required: required.has(name) }),
        );
      }
    }

    for (const keyword of ['oneOf', 'anyOf'] as const) {
      const variants = schema[keyword];
      if (!Array.isArray(variants)) continue;
      variants.forEach((variant, position) => {
        if (!isObject(variant)) return;
        const name = variantName(schema, variant, resolve(variant, stack).schema, position);
        children.push(
          addNode({ segment: `${keyword}:${position}`, parent, kind: 'variant', name, raw: variant, stack }),
        );
      });
    }

    if (isObject(schema.additionalProperties)) {
      const raw = schema.additionalProperties;
      children.push(addNode({ segment: '*', parent, kind: 'property', name: '[key: string]', raw, stack }));
    }

    // Array items are flattened into the array node, mirroring how the docs show "array object[]"
    if (schema.type === 'array' && isObject(schema.items)) {
      const items = resolve(schema.items, stack);
      if (!items.circular) children.push(...addSchemaChildren(items.schema, parent, items.stack));
    }

    return children;
  };

  const addResponseNode = (status: string, response: JsonObject, parent: string, name: string) => {
    const responseSchema = getJsonMediaSchema(response);
    const schema = responseSchema ?? {};
    const description = typeof response.description === 'string' ? response.description : undefined;
    return addNode({
      segment: status,
      parent,
      kind: 'response',
      name,
      // Fall back to the response description when the schema has none of its own
      raw: description && !('description' in schema) ? { ...schema, description } : schema,
      moduleSource: response,
      typeLabel: responseSchema ? undefined : 'no content',
      stack: [],
    });
  };

  const responseEntries = (op: JsonObject) =>
    Object.entries(isObject(op.responses) ? op.responses : {}).filter((entry): entry is [string, JsonObject] =>
      isObject(entry[1]),
    );

  /** The request body, then every response grouped together */
  const addInputChildren = (op: JsonObject, parent: string): TreeNodeData[] => {
    const children: TreeNodeData[] = [];

    const bodySchema = getJsonMediaSchema(op.requestBody);
    if (bodySchema) {
      children.push(
        addNode({
          segment: 'requestBody',
          parent,
          kind: 'requestBody',
          name: 'Request body',
          raw: bodySchema,
          required: isObject(op.requestBody) && op.requestBody.required === true,
          stack: [],
        }),
      );
    }

    if (isObject(op.responses)) {
      const responsesNode = addGroupNode({
        value: `${parent}/responses`,
        parent,
        kind: 'responses',
        name: 'Responses',
        ownModules: [],
        subtreeModules: index.subtreeModules.get(op.responses) ?? NO_MODULES,
      });
      const responseChildren = responseEntries(op).map(([status, response]) =>
        addResponseNode(status, response, responsesNode.value, status),
      );
      children.push(withChildren(responsesNode, responseChildren));
    }

    return children;
  };

  /** The successful responses describe what the operation returns, so they hang straight off the operation */
  const addOutputChildren = (op: JsonObject, parent: string): TreeNodeData[] =>
    responseEntries(op)
      .filter(([status]) => isSuccessStatus(status))
      .map(([status, response]) => addResponseNode(status, response, parent, `${status} response`));

  const data: TreeNodeData[] = [];
  const paths = isObject(spec.paths) ? spec.paths : {};

  for (const [path, pathItem] of Object.entries(paths)) {
    if (!isObject(pathItem)) continue;
    for (const method of HTTP_METHODS) {
      const op = pathItem[method];
      if (!isObject(op)) continue;

      const opNode = addGroupNode({
        value: escapePointerSegment(`${method} ${path}`),
        parent: null,
        kind: 'operation',
        name: `${method.toUpperCase()} ${path}`,
        ownModules: toModules(op['x-modules']),
        subtreeModules: index.subtreeModules.get(op) ?? NO_MODULES,
        operation: {
          method,
          path,
          summary: typeof op.summary === 'string' ? op.summary : undefined,
          description: typeof op.description === 'string' ? op.description : undefined,
          operationId: typeof op.operationId === 'string' ? op.operationId : undefined,
        },
      });
      const children = view === 'outputs' ? addOutputChildren(op, opNode.value) : addInputChildren(op, opNode.value);
      data.push(withChildren(opNode, children));
    }
  }

  return { data, nodes };
}

/** Short type summary in the style of the reference docs, e.g. "string · enum" or "array object[]" */
function describeType(schema: JsonObject, refName?: string): string {
  const baseType = (s: JsonObject): string => {
    if (Array.isArray(s.type)) return s.type.join(' | ');
    if (typeof s.type === 'string') return s.type;
    if ('const' in s) return typeof s.const;
    if (Array.isArray(s.oneOf) || Array.isArray(s.anyOf)) return 'one of';
    if (isObject(s.properties)) return 'object';
    // {"not": {}} matches nothing, i.e. the field must be left out
    if (isObject(s.not) && Object.keys(s.not).length === 0) return 'never';
    return refName ?? 'any';
  };

  const type = baseType(schema);
  if (type === 'array') {
    const items = isObject(schema.items) ? schema.items : {};
    const itemType = typeof items.$ref === 'string' ? 'object' : baseType(items);
    return `array ${itemType === 'one of' ? 'object' : itemType}[]`;
  }
  if ('const' in schema) return `${type} · const`;
  if (Array.isArray(schema.enum)) return `${type} · enum`;
  return type;
}

/**
 * How a node relates to the selected modules:
 * - match: the node itself is tagged with a selected module
 * - inherited: an ancestor matched, so the node is part of a selected field
 * - contains: something beneath the node matched
 * - none: unrelated to the selection
 */
export type ModuleHighlight = 'match' | 'inherited' | 'contains' | 'none';

export function computeHighlights(tree: SpecTree, selected: ReadonlySet<ModuleId>): Map<string, ModuleHighlight> {
  const highlights = new Map<string, ModuleHighlight>();

  // Parents are visited before their children, so an ancestor's highlight is always known
  for (const node of tree.nodes.values()) {
    const parent = node.parent === null ? undefined : highlights.get(node.parent);
    if (hasAnySelected(node.ownModules, selected)) {
      highlights.set(node.value, 'match');
    } else if (parent === 'match' || parent === 'inherited') {
      highlights.set(node.value, 'inherited');
    } else if (parent === 'contains' && isDiscriminator(node.schema)) {
      // Kept alongside the matches, as the docs filter does
      highlights.set(node.value, 'inherited');
    } else if (hasAnySelected(node.subtreeModules, selected)) {
      highlights.set(node.value, 'contains');
    } else {
      highlights.set(node.value, 'none');
    }
  }

  return highlights;
}

/** Counts how many fields in the tree are tagged with each module */
export function countModuleFields(tree: SpecTree): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of tree.nodes.values()) {
    node.ownModules.forEach((m) => counts.set(m, (counts.get(m) ?? 0) + 1));
  }
  return counts;
}

/** Nodes from the root down to, but not including, the node with the given value */
export function getAncestors(nodes: ReadonlyMap<string, SpecNode>, value: string): SpecNode[] {
  const ancestors: SpecNode[] = [];
  let node = nodes.get(value);
  while (node?.parent) {
    node = nodes.get(node.parent);
    if (node) ancestors.unshift(node);
  }
  return ancestors;
}
